import React, { useRef } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

interface Props {
    label: string;
    searchFunction: (searchText: string) => Promise<readonly ListElementType[]>;
    values: ListElementType[];
    changeValues: (values: ListElementType[]) => void;
    sx?: object;
}

const CustomAutocomplete = (props: Props) => {

    const SEARCH_DELAY = 1000;

    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<readonly ListElementType[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [inputValue, setInputValue] = React.useState<string>('');
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleClose = () => {
        setOpen(false);
    };

    const handleUpdateOptions = async () => {
        setLoading(true);
        const list = await props.searchFunction(inputValue);
        setOptions(list);
        setLoading(false);
    }

    const onChangeInputValue = (searchText: string) => {
        setInputValue(searchText);
        setLoading(true);

        if (searchText.length < 3) {
            setOpen(false);
            return;
        } else {
            setOpen(true);
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            handleUpdateOptions();
        }, SEARCH_DELAY);
    }

    return (
        <Autocomplete
            multiple
            sx={props.sx}
            open={open}
            onClose={handleClose}
            isOptionEqualToValue={(option, value) => option.title === value.title}
            getOptionLabel={(option) => option.title}
            options={options}
            loading={loading}
            value={props.values}
            onChange={(e, item) => props.changeValues(item)}
            getOptionDisabled={(option) => option.isDisabled === true}
            renderInput={(params) => (
                <TextField
                {...params}
                label={props.label}
                onChange={(e) => onChangeInputValue(e.target.value)}
                slotProps={{
                    input: {
                    ...params.InputProps,
                    endAdornment: (
                        <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                        </React.Fragment>
                    ),
                    },
                }}
                />
            )}
        />
    );
}

export default CustomAutocomplete;